<template>
  <div
    v-if="
      !generalStatus?.has_active_coi_on_file &&
      !generalStatus?.has_pending_coi_request
    "
    class="flex flex-col items-center justify-center gap-6 rounded-md border border-steel-300 py-8"
    :class="isTabletOrMobile ? 'h-[600px]' : ''"
  >
    <img
      src="@/assets/file-your-coi.svg"
      alt="File your COI"
    />
    <div
      :class="isTabletOrMobile ? 'text-center' : 'flex flex-col items-center'"
    >
      <h1 class="mb-2 text-xl font-medium text-neutral-boldest">
        File your COI
      </h1>
      <p class="px-2 text-sm font-normal text-neutral-subtle-bold lg:px-0">
        Provide the name of your insurance agency and their contact
        <br v-if="!isTabletOrMobile" />
        information so that we can request your Certificate of Insurance (COI).
      </p>
    </div>
    <div
      :class="[
        'flex w-full gap-3',
        isTabletOrMobile
          ? 'flex-col-reverse px-2'
          : 'flex-row items-center justify-center',
      ]"
    >
      <button
        :class="[
          'rounded-md text-sm font-medium',
          isTabletOrMobile
            ? 'h-12 w-full bg-steel-20 text-neutral-subtle-bold'
            : 'h-10 bg-steel-20 px-3 text-neutral-subtle-bold hover:bg-hovered-gray active:bg-pressed-gray',
        ]"
        @click="onLearnMoreCOI"
      >
        Learn more
      </button>

      <button
        :class="[
          'rounded-md text-sm font-medium',
          isTabletOrMobile
            ? 'h-12 w-full bg-neutral-subtle-yellow text-neutral-boldest'
            : 'h-10 bg-neutral-subtle-yellow px-3 text-neutral-boldest hover:bg-neutral-subtle-yellow-bold active:bg-neutral-subtle-yellow-boldest',
        ]"
        @click="emit('openInsuranceModal')"
      >
        Submit COI Request
      </button>
    </div>
  </div>
  <div
    v-else-if="
      !generalStatus?.has_active_coi_on_file &&
      generalStatus?.has_pending_coi_request
    "
    class="flex flex-col items-center justify-center gap-6 rounded-md border border-steel-300 py-8"
  >
    <img
      src="@/assets/pending-coi-request.svg"
      alt="Pending COI Request"
    />
    <div
      :class="isTabletOrMobile ? 'text-center' : 'flex flex-col items-center'"
    >
      <h1 class="mb-2 text-xl font-medium text-neutral-boldest">
        Pending COI Request
      </h1>
      <p class="text-sm font-normal text-neutral-subtle-bold">
        Please instruct your insurance agency to email your
        <br v-if="!isTabletOrMobile" />
        Certificate of Insurance (COI) to
        <a
          href="mailto:insurance@certs.highway.com"
          class="text-sun-700 underline hover:opacity-90"
          >insurance@certs.highway.com</a
        >
      </p>
    </div>
  </div>
  <div
    v-else
    id="certificate"
    class="toc-section"
  >
    <div v-if="activePolicies?.length > 0">
      <section class="flex flex-col text-left">
        <div
          class="flex flex-col items-start justify-start gap-4 lg:gap-0 xl:flex-row"
        >
          <div class="items-left flex w-full flex-col justify-between gap-1">
            <div
              class="relative flex w-full items-center justify-between rounded-xl bg-white p-3 lg:rounded-md"
              style="border: 1px solid rgba(31, 36, 46, 0.14)"
            >
              <div class="flex w-full flex-col items-start justify-start">
                <h3
                  class="text-left text-xs font-medium uppercase text-steel-1100"
                >
                  Certificate of Insurance
                </h3>
                <p class="text-xs font-normal text-steel-700">
                  Received on:
                  {{ formattedActiveDate }}
                </p>
              </div>
              <div class="flex w-full items-center justify-end gap-2">
                <p
                  v-if="!loading"
                  class="mr-2 text-left text-xs text-steel-900"
                >
                  Last updated:
                  {{ dayjs(policyUpdatedAt).format('MM/DD/YY, h:mm a') }}
                </p>
                <button
                  class="flex h-10 items-center gap-1 rounded-md bg-steel-20 px-3 text-sm font-medium text-neutral-subtle-bold hover:bg-hovered-gray active:bg-pressed-gray"
                  @click.stop="emit('refreshInsurance')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M11.8284 2.99951L14.6569 5.82794L11.8284 8.65637V7.00227C10.5013 7.04021 9.22242 7.51746 8.19365 8.36175C7.12061 9.24237 6.38611 10.4678 6.1153 11.8293C5.84449 13.1907 6.05412 14.604 6.70849 15.8282C7.36285 17.0524 8.42145 18.0119 9.70391 18.5431C10.9864 19.0743 12.4134 19.1444 13.7417 18.7415C15.0701 18.3385 16.2176 17.4874 16.9888 16.3332C17.76 15.179 18.1072 13.7932 17.9711 12.4117C17.8351 11.0303 17.2242 9.73873 16.2427 8.75717L17.6569 7.34296C18.9656 8.6517 19.7801 10.3737 19.9615 12.2157C20.1429 14.0576 19.68 15.9055 18.6518 17.4444C17.6235 18.9833 16.0934 20.1181 14.3223 20.6553C12.5511 21.1926 10.6485 21.0991 8.93855 20.3909C7.22859 19.6826 5.81712 18.4033 4.94464 16.771C4.07216 15.1387 3.79265 13.2544 4.15373 11.4391C4.51481 9.62381 5.49414 7.98989 6.92486 6.81573C8.31133 5.67789 10.0386 5.04002 11.8284 5.00165V2.99951Z"
                      fill="#6C7C9D"
                    />
                  </svg>
                  <span class="text-steel-700">Refresh</span>
                </button>
              </div>
            </div>
            <p class="mt-2 text-xs font-normal text-steel-800">
              Here are the specific vehicles covered by your BIPD insurance
              policy. Vehicles absent from the schedule provided on your COI
              will not be insured.
            </p>
          </div>

          <!-- <button
            :class="[
              'rounded-md bg-neutral-subtle-bold px-3 text-sm font-medium text-white hover:bg-neutral-blue active:bg-neutral-boldest',
              isTabletOrMobile ? 'h-12 w-full' : 'h-10',
            ]"
            @click="onOpenUpdateInsurance"
          >
            Update information
          </button> -->
        </div>

        <div>
          <section
            v-if="loading"
            class="grid grid-cols-auto-fill-300 gap-4 py-4"
          >
            <InsuranceCardSkeleton
              v-for="i in 10"
              :key="i"
            />
          </section>
          <section
            v-if="!loading && activePolicies?.length > 0"
            class="grid grid-cols-auto-fill-300 gap-4 py-4"
          >
            <div
              v-for="item in activePolicies"
              :key="item.id"
              class="w-full text-left"
            >
              <insurance-card :policy="item" />
            </div>
          </section>

          <section
            v-else-if="insurance?.fmcsa_active_and_pending_insurance.length > 0"
            class="grid grid-cols-auto-fill-300 gap-4 py-4"
          >
            <div
              v-for="item in insurance.fmcsa_active_and_pending_insurance"
              :key="item.id"
              class="w-full"
            >
              <insurance-government-card :insurance="item" />
            </div>
          </section>
        </div>
      </section>
    </div>
  </div>

  <!-- <file-insurance-information-modal
    v-if="insuranceModalVisible"
    :visible="insuranceModalVisible"
    @close="onCloseUpdateInsuranceModalVisible"
    @submit="handleUpdateInsuranceModalVisible"
  /> -->
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { useStore } from 'vuex';
import _ from 'lodash';
// import { useToast } from 'vue-toast-notification';
import { useScreenSize } from '@/composables/useScreenSize';
import InsuranceCard from '@/components/InsuranceCard.vue';

import InsuranceGovernmentCard from '@/components/InsuranceGovernmentCard.vue';
import InsuranceCardSkeleton from '@/components/InsuranceCardSkeleton.vue';
// import FileInsuranceInformationModal from '@/components/modals/FileInsuranceInformationModal.vue';
import dayjs from 'dayjs';

const store = useStore();
// const toast = useToast();
const { screenSize } = useScreenSize();

defineProps({
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['openInsuranceModal', 'refreshInsurance']);

// const insuranceModalVisible = ref(false);

// const displayInsuranceModal = computed(() => {
//   return store.state.insurance.displayInsuranceModal;
// });

const generalStatus = computed(
  () => store.state.users.userLoadBoardAccess?.user
);

const insurancePolicies = computed(
  () => store.state.insurance?.carrierInsurancePolicies || []
);

const customSort = (a, b) => {
  if (a?.status === 'pending_active' && b?.status !== 'pending_active') {
    return 1;
  }
  if (a?.status !== 'pending_active' && b?.status === 'pending_active') {
    return -1;
  }
  return 0;
};

const insurancePoliciesFiltered = computed(() => {
  const sorted = _.sortBy(insurancePolicies.value, function (element) {
    var rank = {
      commercial_general_liability: 1,
      automobile_liability: 2,
      motor_truck_cargo: 3,
      workers_compensation: 4,
      trailer_interchange: 5,
    };
    return rank[element.is_type];
  });

  return sorted.sort(customSort);
});
const activePolicies = computed(() => {
  return insurancePoliciesFiltered.value.filter(
    (policy) => policy.status !== 'inactive'
  );
});
const formatDate = (date) => {
  return dayjs(date).format('MM/DD/YY');
};
const formattedActiveDate = computed(() => {
  return formatDate(
    activePolicies.value?.[0]?.insurance_certificate?.received_at
  );
});

const policyUpdatedAt = computed(
  () => activePolicies.value?.[0]?.insurance_certificate?.updated_at
);

const isTabletOrMobile = computed(
  () => screenSize.value === 'tablet' || screenSize.value === 'mobile'
);

// function onOpenUpdateInsurance() {
//   insuranceModalVisible.value = true;
// }

function onLearnMoreCOI() {
  window.open(
    `https://service.highway.com/en/collections/10316575-insurance`,
    '_blank'
  );
}

// async function handleUpdateInsuranceModalVisible(agency) {
//   const { name, email, phoneNumber } = agency;

//   const payload = {
//     carrier_id: store.state.users?.currentUser?.company?.carrier_id,
//     contact_email: email,
//     contact_phone: phoneNumber,
//     contact_name: name,
//     company_ids: [1],
//     is_type: 'highway',
//   };

//   try {
//     const res = await store.dispatch(
//       'insurance/insuranceCertificateRequest',
//       payload
//     );

//     if ([200, 201, 204].includes(res.status)) {
//       await store.dispatch('users/checkLoadBoardAccess');
//       toast?.info('Request sent', {
//         position: 'top-right',
//         timeout: 5000,
//       });
//     }
//   } catch (error) {
//     console.error('Error sending insurance request:', error);
//     toast?.error('An error occurred. Please try again.', {
//       position: 'top-right',
//       timeout: 5000,
//     });
//   } finally {
//     onCloseUpdateInsuranceModalVisible();
//   }
// }

// function onCloseUpdateInsuranceModalVisible() {
//   insuranceModalVisible.value = false;
// }

// watch(displayInsuranceModal, (newVal) => {
//   if (newVal) {
//     onOpenUpdateInsurance();
//   }
// });
</script>
